import React from 'react';
import { useSocket, STORAGE_KEYS, clearPlayerSeat } from '../contexts/SocketContext';
import { formatString, PlayerRole, GameType, MIN_PLAYERS_SIMILAR_MINDS, MIN_PLAYERS_ACE_ATTORNEY, MIN_PLAYERS_GAME_THEORY_WHEEL, MAX_PLAYERS, STAND_APART_CARD_COUNT_MIN, STAND_APART_CARD_COUNT_MAX, STAND_APART_CARD_COUNT_DEFAULT, SCHRODINGER_DURATION_CEILING, SCHRODINGER_DURATION_FLOOR, SCHRODINGER_DURATION_MAX_DEFAULT, SCHRODINGER_DURATION_MIN_DEFAULT, getNashMode, nashPlayerLimits, type NashMode } from '@deadpan/shared';
import { useTranslations } from '../hooks/useTranslations';
import { DeadpanConfig } from './deadpan/DeadpanConfig';
import { UndercoverConfig } from './undercover/UndercoverConfig';
import { SimilarMindsConfig } from './similarMinds/SimilarMindsConfig';
import { publicInviteUrlForRoomCode } from '../utils/roomInvitePath';

export const RoomLobby: React.FC = () => {
  const { socket, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const isHost = !!currentRoom && currentRoom.hostId === myPlayerId;
  const [inviteCopied, setInviteCopied] = React.useState(false);
  const inviteCopiedTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [explanationTime, setExplanationTime] = React.useState(currentRoom?.timerConfig?.explanationDisplayTime || 15);
  const [prepTime, setPrepTime] = React.useState(currentRoom?.timerConfig?.prepTime || 20);
  const [isEditingExplanation, setIsEditingExplanation] = React.useState(false);
  const [isEditingPrep, setIsEditingPrep] = React.useState(false);
  const [isEditingBombRelay, setIsEditingBombRelay] = React.useState(false);
  const [isEditingBombRelayProbability, setIsEditingBombRelayProbability] = React.useState(false);
  const [timerError, setTimerError] = React.useState<string | null>(null);
  const [configError, setConfigError] = React.useState<string | null>(null);
  const [editingPlayerId, setEditingPlayerId] = React.useState<string | null>(null);
  const [editingName, setEditingName] = React.useState<string>('');
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [gameType, setGameType] = React.useState<GameType>(currentRoom?.gameType || GameType.DEADPAN);
  const [undercoverCount, setUndercoverCount] = React.useState(
    currentRoom?.undercoverConfig?.undercoverCount !== undefined 
      ? currentRoom.undercoverConfig.undercoverCount 
      : 1
  );
  const [blankCount, setBlankCount] = React.useState(currentRoom?.undercoverConfig?.blankCount || 0);
  const [undercoverMode, setUndercoverMode] = React.useState<'classic' | 'ghost'>(currentRoom?.undercoverConfig?.mode || 'classic');
  const [factInputSeconds, setFactInputSeconds] = React.useState<number>(currentRoom?.undercoverConfig?.factInputSeconds || 60);
  const [isEditingFactTimer, setIsEditingFactTimer] = React.useState(false);

  React.useEffect(() => {
    if (gameType !== GameType.UNDERCOVER) {
      setIsEditingFactTimer(false);
    }
  }, [gameType]);
  const [bombRelayTime, setBombRelayTime] = React.useState(currentRoom?.timerConfig?.bombRelayTime || 90);
  const [bombRelayLevel1Probability, setBombRelayLevel1Probability] = React.useState(currentRoom?.bombRelayConfig?.level1Probability ?? 90);
  const [pensieveMemoryMode, setPensieveMemoryMode] = React.useState<'others' | 'self'>(
    currentRoom?.pensieveConfig?.memoryMode ?? 'others',
  );
  const [similarMindsAnswerSeconds, setSimilarMindsAnswerSeconds] = React.useState(
    currentRoom?.similarMindsConfig?.answerInputSeconds ?? 30,
  );
  const [similarMindsVotingSeconds, setSimilarMindsVotingSeconds] = React.useState(
    currentRoom?.similarMindsConfig?.votingSeconds ?? 20,
  );
  const [similarMindsVotesPerPlayer, setSimilarMindsVotesPerPlayer] = React.useState(
    currentRoom?.similarMindsConfig?.votesPerPlayer ?? 2,
  );
  const [similarMindsFirstQuestionHost, setSimilarMindsFirstQuestionHost] = React.useState<
    'room_host' | 'random'
  >(currentRoom?.similarMindsConfig?.firstQuestionHost ?? 'room_host');
  const [isEditingSimilarMindsAnswer, setIsEditingSimilarMindsAnswer] = React.useState(false);
  const [isEditingSimilarMindsVoting, setIsEditingSimilarMindsVoting] = React.useState(false);
  const [nashMode, setNashMode] = React.useState<NashMode>(
    currentRoom ? getNashMode(currentRoom) : 'wheel',
  );
  const [standApartCardCount, setStandApartCardCount] = React.useState(
    currentRoom?.nashConfig?.cardCount ?? STAND_APART_CARD_COUNT_DEFAULT,
  );
  const [schrodingerMinSeconds, setSchrodingerMinSeconds] = React.useState(
    currentRoom?.nashConfig?.schrodingerMinSeconds ?? SCHRODINGER_DURATION_MIN_DEFAULT,
  );
  const [schrodingerMaxSeconds, setSchrodingerMaxSeconds] = React.useState(
    currentRoom?.nashConfig?.schrodingerMaxSeconds ?? SCHRODINGER_DURATION_MAX_DEFAULT,
  );
  const [isEditingSchrodingerRange, setIsEditingSchrodingerRange] = React.useState(false);

  const similarMindsParticipantCount = currentRoom
    ? currentRoom.players.filter((p) => !p.isSpectator).length
    : 0;
  const similarMindsMaxVotesPerPlayer = Math.max(1, similarMindsParticipantCount - 1);

  React.useEffect(() => {
    if (currentRoom?.timerConfig && !isEditingExplanation && !isEditingPrep && !isEditingBombRelay) {
      setExplanationTime(currentRoom.timerConfig.explanationDisplayTime);
      setPrepTime(currentRoom.timerConfig.prepTime);
      setBombRelayTime(currentRoom.timerConfig.bombRelayTime || 90);
    }
    if (currentRoom?.bombRelayConfig && !isEditingBombRelayProbability) {
      setBombRelayLevel1Probability(currentRoom.bombRelayConfig.level1Probability ?? 90);
    }
    if (currentRoom?.gameType) {
      setGameType(currentRoom.gameType);
    }
    if (currentRoom?.undercoverConfig) {
      const newUndercoverCount = currentRoom.undercoverConfig.undercoverCount !== undefined 
        ? currentRoom.undercoverConfig.undercoverCount 
        : 1;
      const newBlankCount = currentRoom.undercoverConfig.blankCount !== undefined 
        ? currentRoom.undercoverConfig.blankCount 
        : 0;
      // Only update if values have actually changed to avoid resetting user input
      if (undercoverCount !== newUndercoverCount) {
        setUndercoverCount(newUndercoverCount);
      }
      if (blankCount !== newBlankCount) {
        setBlankCount(newBlankCount);
      }
      setUndercoverMode(currentRoom.undercoverConfig.mode || 'classic');
      if (!isEditingFactTimer) {
        setFactInputSeconds(currentRoom.undercoverConfig.factInputSeconds || 60);
      }
    }
    if (currentRoom?.pensieveConfig?.memoryMode) {
      setPensieveMemoryMode(currentRoom.pensieveConfig.memoryMode);
    }
    if (currentRoom?.similarMindsConfig) {
      if (!isEditingSimilarMindsAnswer) {
        setSimilarMindsAnswerSeconds(currentRoom.similarMindsConfig.answerInputSeconds ?? 30);
      }
      if (!isEditingSimilarMindsVoting) {
        setSimilarMindsVotingSeconds(currentRoom.similarMindsConfig.votingSeconds ?? 20);
      }
      const configuredVotes = currentRoom.similarMindsConfig.votesPerPlayer ?? 2;
      const maxVotes = Math.max(1, currentRoom.players.filter((p) => !p.isSpectator).length - 1);
      setSimilarMindsVotesPerPlayer(Math.max(1, Math.min(maxVotes, configuredVotes)));
      setSimilarMindsFirstQuestionHost(currentRoom.similarMindsConfig.firstQuestionHost ?? 'room_host');
    }
    if (currentRoom?.nashConfig) {
      setNashMode(getNashMode(currentRoom));
      if (currentRoom.nashConfig.cardCount != null) {
        setStandApartCardCount(currentRoom.nashConfig.cardCount);
      }
      if (!isEditingSchrodingerRange) {
        setSchrodingerMinSeconds(currentRoom.nashConfig.schrodingerMinSeconds ?? SCHRODINGER_DURATION_MIN_DEFAULT);
        setSchrodingerMaxSeconds(currentRoom.nashConfig.schrodingerMaxSeconds ?? SCHRODINGER_DURATION_MAX_DEFAULT);
      }
    } else if (currentRoom?.gameType === GameType.GAME_THEORY_WHEEL) {
      setNashMode('wheel');
    }
  }, [currentRoom?.timerConfig, currentRoom?.bombRelayConfig, currentRoom?.gameType, currentRoom?.undercoverConfig, currentRoom?.pensieveConfig, currentRoom?.similarMindsConfig, currentRoom?.nashConfig, currentRoom?.players, isEditingExplanation, isEditingPrep, isEditingBombRelay, isEditingBombRelayProbability, isEditingFactTimer, isEditingSimilarMindsAnswer, isEditingSimilarMindsVoting, isEditingSchrodingerRange]);

  // Validate undercover config when values change
  // Note: Wordsmith is excluded from role assignment, so we need to account for that
  React.useEffect(() => {
    if (gameType === GameType.UNDERCOVER && currentRoom) {
      const totalPlayers = currentRoom.players.length;
      // Wordsmith is excluded from role assignment (see UndercoverGameManager)
      const hasWordsmith = !!currentRoom.undercoverConfig?.wordsmithId;
      const mode = currentRoom.undercoverConfig?.mode || undercoverMode;
      const playersForRoleAssignment = hasWordsmith ? totalPlayers - 1 : totalPlayers;
      const civilianCount = playersForRoleAssignment - undercoverCount - blankCount;

      if (mode === 'ghost') {
        const ghostCivilians = totalPlayers - undercoverCount;
        if (blankCount !== 0 || hasWordsmith) {
          setConfigError(t.games.undercover.ghostErrorNoBlankOrWordsmith);
        } else if (undercoverCount < 1) {
          setConfigError(t.games.undercover.ghostErrorMinOneUndercover);
        } else if (ghostCivilians <= undercoverCount) {
          setConfigError(t.games.undercover.errorCivilianMustExceedOpposition);
        } else if (factInputSeconds < 30 || factInputSeconds > 180) {
          setConfigError(t.games.undercover.ghostErrorFactTimerRange);
        } else {
          setConfigError(null);
        }
        return;
      }
      
      if (undercoverCount < 0 || blankCount < 0 || civilianCount < 0) {
        setConfigError(t.games.undercover.errorNegativeCount);
      } else if (hasWordsmith && undercoverCount === 0) {
        setConfigError(t.games.undercover.errorWordsmithRequiresUndercover);
      } else {
        const totalRoles = undercoverCount + blankCount + civilianCount;
        if (totalRoles !== playersForRoleAssignment) {
          const typeText = hasWordsmith ? `${t.games.undercover.playersForRoleAssignment} ` : '';
          setConfigError(formatString(t.games.undercover.errorRoleSumMismatch, { 
            expected: String(playersForRoleAssignment),
            actual: String(totalRoles),
            type: typeText
          }));
        } else if (civilianCount <= undercoverCount + blankCount) {
          setConfigError(t.games.undercover.errorCivilianMustExceedOpposition);
        } else {
          setConfigError(null);
        }
      }
    } else {
      setConfigError(null);
    }
  }, [gameType, undercoverCount, blankCount, undercoverMode, factInputSeconds, currentRoom, t]);

  React.useEffect(() => {
    return () => {
      if (inviteCopiedTimeoutRef.current) {
        clearTimeout(inviteCopiedTimeoutRef.current);
      }
    };
  }, []);

  const handleLeaveRoom = () => {
    if (socket) {
      socket.emit('client:leave-room');
      clearPlayerSeat();
    }
  };

  const handleCopyInviteLink = async () => {
    if (!currentRoom) return;
    const text = publicInviteUrlForRoomCode(currentRoom.code);
    const markCopied = () => {
      setInviteCopied(true);
      if (inviteCopiedTimeoutRef.current) clearTimeout(inviteCopiedTimeoutRef.current);
      inviteCopiedTimeoutRef.current = setTimeout(() => setInviteCopied(false), 2000);
    };

    try {
      await navigator.clipboard.writeText(text);
      markCopied();
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        markCopied();
      } catch {
        // ignore
      }
    }
  };

  const handleStartEditName = (playerId: string, currentName: string) => {
    if (playerId !== myPlayerId) return; // Only allow editing own name
    setEditingPlayerId(playerId);
    setEditingName(currentName);
    setNameError(null);
  };

  const handleCancelEditName = () => {
    setEditingPlayerId(null);
    setEditingName('');
    setNameError(null);
  };

  const handleSaveName = () => {
    if (!socket || !editingPlayerId || !editingName.trim()) {
      setNameError(t.ui.errors.playerNameRequired);
      return;
    }

    const trimmedName = editingName.trim();
    
    // Check if name is the same
    const currentPlayer = currentRoom?.players.find(p => p.id === editingPlayerId);
    if (currentPlayer?.name.toLowerCase() === trimmedName.toLowerCase()) {
      handleCancelEditName();
      return;
    }

    // Check for duplicate name in current room
    const duplicatePlayer = currentRoom?.players.find(
      p => p.id !== editingPlayerId && 
      p.name.toLowerCase() === trimmedName.toLowerCase() &&
      p.isConnected
    );

    if (duplicatePlayer) {
      setNameError(t.ui.errors.duplicatePlayerName);
      return;
    }

    setNameError(null);
    socket.emit('client:update-player-name', trimmedName);
    
    // Update sessionStorage
    sessionStorage.setItem(STORAGE_KEYS.PLAYER_NAME, trimmedName);
    
    handleCancelEditName();
  };

  // Listen for errors from server
  React.useEffect(() => {
    if (!socket) return;

    const handleError = (error: string) => {
      if (error.includes('name') || error.includes('Name') || error.includes('player')) {
        setNameError(error);
      }
    };

    socket.on('server:error', handleError);
    return () => {
      socket.off('server:error', handleError);
    };
  }, [socket]);

  const TIMER_MIN = 5;
  const TIMER_MAX = 120;
  const BOMB_RELAY_TIMER_MIN = 0;
  const BOMB_RELAY_TIMER_MAX = 1000;
  const BOMB_RELAY_LEVEL1_MIN = 0;
  const BOMB_RELAY_LEVEL1_MAX = 100;
  const timerErrorMessage = t.ui.timerOutOfRange || 'Timer must be between 5 and 120 seconds';
  const bombRelayTimerErrorMessage = t.games.bombRelay.timerOutOfRange;
  const bombRelayLevel1ProbabilityErrorMessage = t.games.bombRelay.level1ProbabilityOutOfRange;

  const isValidTimerValue = (value: number): boolean => value >= TIMER_MIN && value <= TIMER_MAX;
  const isValidBombRelayTimerValue = (value: number): boolean => value >= BOMB_RELAY_TIMER_MIN && value <= BOMB_RELAY_TIMER_MAX;
  const isValidBombRelayLevel1Probability = (value: number): boolean => value >= BOMB_RELAY_LEVEL1_MIN && value <= BOMB_RELAY_LEVEL1_MAX;

  const isValidUndercoverConfig = (): boolean => {
    if (gameType !== GameType.UNDERCOVER) return true;
    if (!currentRoom) return true;
    const totalPlayers = currentRoom.players.length;
    // Wordsmith is excluded from role assignment (see UndercoverGameManager)
    const hasWordsmith = !!currentRoom.undercoverConfig?.wordsmithId;
    const mode = currentRoom.undercoverConfig?.mode || undercoverMode;
    if (mode === 'ghost') {
      const ghostCivilians = totalPlayers - undercoverCount;
      return (
        undercoverCount >= 1 &&
        blankCount === 0 &&
        !hasWordsmith &&
        ghostCivilians > undercoverCount &&
        factInputSeconds >= 30 &&
        factInputSeconds <= 180
      );
    }
    const playersForRoleAssignment = hasWordsmith ? totalPlayers - 1 : totalPlayers;
    const civilianCount = playersForRoleAssignment - undercoverCount - blankCount;
    
    // Validate: no negative values
    if (undercoverCount < 0 || blankCount < 0 || civilianCount < 0) {
      return false;
    }
    // Validate: if wordsmith exists, must have at least one undercover
    if (hasWordsmith && undercoverCount === 0) {
      return false;
    }
    // Validate: sum of all roles must equal playersForRoleAssignment
    const totalRoles = undercoverCount + blankCount + civilianCount;
    if (totalRoles !== playersForRoleAssignment) {
      return false;
    }
    // Validate: civilian count must be greater than sum of undercover and blank counts
    if (civilianCount <= undercoverCount + blankCount) {
      return false;
    }
    return true;
  };

  const isBombRelayTimerValid = (): boolean => isValidBombRelayTimerValue(bombRelayTime);
  const isBombRelayLevel1ProbabilityValid = (): boolean => isValidBombRelayLevel1Probability(bombRelayLevel1Probability);

  const handleStartGame = () => {
    if (!socket) return;
    
    // Validation is already done via isConfigValid, but double-check before starting
    if (!isConfigValid) return;
    
    socket.emit('client:start-game');
  };

  const isSimilarMindsConfigValid = (): boolean => {
    return (
      similarMindsAnswerSeconds >= 0 &&
      similarMindsAnswerSeconds <= 120 &&
      similarMindsVotingSeconds >= 0 &&
      similarMindsVotingSeconds <= 120 &&
      similarMindsVotesPerPlayer >= 1 &&
      similarMindsVotesPerPlayer <= similarMindsMaxVotesPerPlayer
    );
  };

  const isDeadpanTimerValid = isValidTimerValue(explanationTime) && isValidTimerValue(prepTime);
  const nashLimits = nashPlayerLimits(nashMode);
  const minPlayersRequired =
    gameType === GameType.SIMILAR_MINDS
      ? MIN_PLAYERS_SIMILAR_MINDS
      : gameType === GameType.ACE_ATTORNEY
        ? MIN_PLAYERS_ACE_ATTORNEY
      : gameType === GameType.GAME_THEORY_WHEEL
        ? nashLimits.min
      : gameType === GameType.PENSIEVE || gameType === GameType.BOMB_RELAY
        ? MIN_PLAYERS_GAME_THEORY_WHEEL
        : 3;
  const maxPlayersForGame =
    gameType === GameType.GAME_THEORY_WHEEL
      ? nashLimits.max
      : MAX_PLAYERS;
  const isConfigValid =
    gameType === GameType.DEADPAN
      ? isDeadpanTimerValid
      : gameType === GameType.UNDERCOVER
      ? isValidUndercoverConfig()
      : gameType === GameType.PENSIEVE || gameType === GameType.GAME_THEORY_WHEEL || gameType === GameType.ACE_ATTORNEY
      ? true
      : gameType === GameType.SIMILAR_MINDS
      ? isSimilarMindsConfigValid()
      : isBombRelayTimerValid() && isBombRelayLevel1ProbabilityValid();

  const handleGameTypeChange = (newGameType: GameType) => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    setGameType(newGameType);
    socket.emit('client:update-game-type', newGameType);
  };

  const handleNashModeChange = (nextMode: NashMode) => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    setNashMode(nextMode);
    socket.emit('client:update-nash-mode', nextMode);
  };

  const handlePensieveModeChange = (mode: 'others' | 'self') => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    setPensieveMemoryMode(mode);
    socket.emit('client:update-pensieve-mode', mode);
  };

  const handlePensieveNewOrder = () => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    socket.emit('client:pensieve-new-order');
  };

  const handleTimerChange = (type: 'explanation' | 'prep', value: number) => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    
    if (!isValidTimerValue(value)) {
      setTimerError(timerErrorMessage);
      return;
    }
    
    setTimerError(null);
    socket.emit('client:update-timer-config', 
      type === 'explanation' ? value : explanationTime,
      type === 'prep' ? value : prepTime
    );
  };

  const handleBombRelayTimerChange = (value: number) => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    if (!isValidBombRelayTimerValue(value)) {
      setTimerError(bombRelayTimerErrorMessage);
      return;
    }
    setTimerError(null);
    socket.emit('client:update-bomb-relay-timer', value);
  };

  const handleBombRelayLevel1ProbabilityChange = (value: number) => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    if (!isValidBombRelayLevel1Probability(value)) {
      setTimerError(bombRelayLevel1ProbabilityErrorMessage);
      return;
    }
    setTimerError(null);
    socket.emit('client:update-bomb-relay-level1-probability', value);
  };

  const createTimerInputHandler = (type: 'explanation' | 'prep', setter: (val: number) => void, currentValue: number) => {
    const setIsEditing = type === 'explanation' ? setIsEditingExplanation : setIsEditingPrep;
    return {
      onFocus: () => {
        setIsEditing(true);
        setTimerError(null);
      },
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        if (inputValue === '') {
          setter(0);
          setTimerError(null);
          return;
        }
        const val = parseInt(inputValue);
        if (!isNaN(val)) {
          setter(val);
          if (!isValidTimerValue(val)) {
            setTimerError(timerErrorMessage);
          } else {
            setTimerError(null);
            handleTimerChange(type, val);
          }
        }
      },
      onBlur: () => {
        setIsEditing(false);
        if (!isValidTimerValue(currentValue)) {
          setTimerError(timerErrorMessage);
        } else {
          setTimerError(null);
          handleTimerChange(type, currentValue);
        }
      },
    };
  };

  if (!currentRoom) {
    return null;
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0 mb-4 sm:mb-6 md:mb-8">
        <div className="w-full sm:w-auto">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              {t.ui.room} {currentRoom.code}
            </h2>
            <button
              type="button"
              onClick={() => void handleCopyInviteLink()}
              className="bg-transparent p-0 text-xl leading-none text-gray-500 transition-colors hover:text-indigo-600"
              aria-label={t.ui.shareInviteLink}
              title={inviteCopied ? t.ui.shareInviteLinkCopied : t.ui.shareInviteLink}
            >
              {inviteCopied ? '✅' : '🔗'}
            </button>
            {isHost && (
              <span className="px-2 sm:px-3 py-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold rounded-full">
                👑 {t.ui.host}
              </span>
            )}
          </div>
          <p className="text-sm sm:text-base text-gray-600 font-medium">
            {currentRoom.players.length} / {maxPlayersForGame}{' '}
            {t.ui.maxPlayers.replace(/^\/\s*\d+\s*/, '')}
          </p>
        </div>
        {!isHost && (
          <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2 sm:items-start">
            <button
              type="button"
              onClick={handleLeaveRoom}
              className="px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base w-full sm:w-auto"
            >
              {t.ui.leaveRoom}
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 sm:mb-6 md:mb-8">
        <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
          <span className="text-xl sm:text-2xl">👥</span>
          {t.ui.players}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentRoom.players.map((player) => (
            <div
              key={player.id}
              className={`p-4 rounded-xl border-2 transition-all ${
                player.id === myPlayerId
                  ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              } ${!player.isConnected ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {editingPlayerId === player.id ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => {
                          setEditingName(e.target.value);
                          setNameError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveName();
                          } else if (e.key === 'Escape') {
                            handleCancelEditName();
                          }
                        }}
                        autoFocus
                        className="flex-1 px-2 py-1 border-2 border-indigo-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-sm font-semibold"
                        maxLength={20}
                      />
                      <button
                        onClick={handleSaveName}
                        className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded-lg hover:bg-green-600 transition-colors"
                        title={t.ui.saveName}
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelEditName}
                        className="px-2 py-1 bg-gray-500 text-white text-xs font-semibold rounded-lg hover:bg-gray-600 transition-colors"
                        title={t.ui.cancel}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <span 
                        className={`font-semibold text-gray-800 ${
                          player.id === myPlayerId 
                            ? 'cursor-pointer hover:text-indigo-600 transition-colors' 
                            : ''
                        }`}
                        onClick={() => player.id === myPlayerId && handleStartEditName(player.id, player.name)}
                        title={player.id === myPlayerId ? t.ui.clickToEditName : ''}
                      >
                        {player.name}
                      </span>
                      {player.id === myPlayerId && (
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
                          {t.ui.you}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!player.isConnected && editingPlayerId !== player.id && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {t.ui.offline}
                    </span>
                  )}
                  {player.id === myPlayerId && editingPlayerId !== player.id && (
                    <span 
                      className="text-xs text-indigo-500 opacity-70 cursor-pointer hover:opacity-100 transition-opacity"
                      onClick={() => handleStartEditName(player.id, player.name)}
                      title={t.ui.clickToEditName}
                    >
                      ✏️
                    </span>
                  )}
                  {isHost && player.id !== myPlayerId && editingPlayerId !== player.id && (
                    <>
                      {currentRoom.phase === 'waiting' && (
                        <button
                          onClick={() => {
                            if (window.confirm(t.ui.transferHostConfirm)) {
                              socket?.emit('client:transfer-host', player.id);
                            }
                          }}
                          className="text-xs text-yellow-500 hover:text-yellow-700 opacity-70 hover:opacity-100 transition-opacity px-2 py-1 rounded hover:bg-yellow-50"
                          title={t.ui.transferHostTooltip}
                        >
                          👑
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (window.confirm(t.ui.removePlayerConfirm)) {
                            socket?.emit('client:kick-player', player.id);
                          }
                        }}
                        className="text-xs text-red-500 hover:text-red-700 opacity-70 hover:opacity-100 transition-opacity px-2 py-1 rounded hover:bg-red-50"
                        title={t.ui.removePlayerTooltip}
                      >
                        🚫
                      </button>
                    </>
                  )}
                </div>
              </div>
              {nameError && editingPlayerId === player.id && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                  {nameError}
                </div>
              )}
              {player.role && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                    player.role === PlayerRole.DEADPAN ? 'bg-purple-100 text-purple-700 border-purple-300' : 
                    player.role === PlayerRole.FACT_SPEAKER ? 'bg-green-100 text-green-700 border-green-300' : 
                    player.role === PlayerRole.DETECTIVE ? 'bg-blue-100 text-blue-700 border-blue-300' :
                    'bg-pink-100 text-pink-700 border-pink-300'
                  }`}>
                    {player.role === PlayerRole.DEADPAN ? t.roles.deadpan : 
                     player.role === PlayerRole.FACT_SPEAKER ? t.roles.fact_speaker : 
                     player.role === PlayerRole.DETECTIVE ? t.roles.detective :
                     t.roles.wordsmith}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {isHost && currentRoom.phase === 'waiting' && (
        <div className="pt-6 border-t-2 border-gray-200">
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
            <h3 className="text-base sm:text-lg font-bold text-blue-800 mb-3 sm:mb-4 flex items-center gap-2">
              <span>🎮</span>
              {t.ui.gameType || t.ui.gameTypeSettings || 'Game Type'}
            </h3>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.ui.selectGameType || 'Select Game'}
              </label>
              <select
                value={gameType}
                onChange={(e) => handleGameTypeChange(e.target.value as GameType)}
                className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:border-blue-500"
              >
                <option value={GameType.DEADPAN}>{t.ui.gameTypeDeadpan || '一本正经'}</option>
                <option value={GameType.UNDERCOVER}>{t.ui.gameTypeUndercover || '谁是卧底'}</option>
                <option value={GameType.BOMB_RELAY}>{t.ui.gameTypeBombRelay}</option>
                <option value={GameType.PENSIEVE}>{t.ui.gameTypePensieve}</option>
                <option value={GameType.SIMILAR_MINDS}>{t.ui.gameTypeSimilarMinds || '言为心声'}</option>
                <option value={GameType.ACE_ATTORNEY}>{t.ui.gameTypeAceAttorney || '逆转裁判'}</option>
                <option value={GameType.GAME_THEORY_WHEEL}>{t.ui.gameTypeGameTheoryWheel || '纳什均衡'}</option>
              </select>
            </div>
          </div>

          {gameType === GameType.GAME_THEORY_WHEEL && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-fuchsia-50 border-2 border-fuchsia-200 rounded-xl">
              <h3 className="text-base sm:text-lg font-bold text-fuchsia-800 mb-3 sm:mb-4">
                {t.games.gameTheoryWheel.settingsTitle}
              </h3>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.games.gameTheoryWheel.modeLabel}
              </label>
              <select
                value={nashMode}
                onChange={(e) => handleNashModeChange(e.target.value as NashMode)}
                className="w-full px-4 py-2 border-2 border-fuchsia-300 rounded-lg focus:outline-none focus:border-fuchsia-500 bg-white"
              >
                <option value="wheel">{t.games.gameTheoryWheel.modeWheel}</option>
                <option value="stand_apart">{t.games.gameTheoryWheel.modeStandApart}</option>
                <option value="schrodinger_croc">{t.games.gameTheoryWheel.modeSchrodingerCroc}</option>
              </select>
              {nashMode === 'schrodinger_croc' && (
                <div className="mt-4 flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t.games.schrodingerCroc.durationMinLabel}
                    </label>
                    <input
                      type="number"
                      min={SCHRODINGER_DURATION_FLOOR}
                      max={SCHRODINGER_DURATION_CEILING}
                      value={schrodingerMinSeconds}
                      disabled={!isHost}
                      onFocus={() => setIsEditingSchrodingerRange(true)}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setSchrodingerMinSeconds(Number.isNaN(val) ? SCHRODINGER_DURATION_MIN_DEFAULT : val);
                      }}
                      onBlur={() => {
                        setIsEditingSchrodingerRange(false);
                        if (!socket || currentRoom.hostId !== myPlayerId) return;
                        socket.emit('client:update-schrodinger-config', schrodingerMinSeconds, schrodingerMaxSeconds);
                      }}
                      className="w-full px-4 py-2 border-2 border-fuchsia-300 rounded-lg focus:outline-none focus:border-fuchsia-500 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t.games.schrodingerCroc.durationMaxLabel}
                    </label>
                    <input
                      type="number"
                      min={SCHRODINGER_DURATION_FLOOR}
                      max={SCHRODINGER_DURATION_CEILING}
                      value={schrodingerMaxSeconds}
                      disabled={!isHost}
                      onFocus={() => setIsEditingSchrodingerRange(true)}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setSchrodingerMaxSeconds(Number.isNaN(val) ? SCHRODINGER_DURATION_MAX_DEFAULT : val);
                      }}
                      onBlur={() => {
                        setIsEditingSchrodingerRange(false);
                        if (!socket || currentRoom.hostId !== myPlayerId) return;
                        socket.emit('client:update-schrodinger-config', schrodingerMinSeconds, schrodingerMaxSeconds);
                      }}
                      className="w-full px-4 py-2 border-2 border-fuchsia-300 rounded-lg focus:outline-none focus:border-fuchsia-500 bg-white disabled:bg-gray-100"
                    />
                  </div>
                  <p className="text-xs text-gray-600">
                    {formatString(t.games.schrodingerCroc.durationHint, {
                      min: String(SCHRODINGER_DURATION_MIN_DEFAULT),
                      max: String(SCHRODINGER_DURATION_MAX_DEFAULT),
                    })}
                  </p>
                </div>
              )}
              {nashMode === 'stand_apart' && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t.games.standApart.cardCountLabel}
                  </label>
                  <select
                    value={standApartCardCount}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setStandApartCardCount(val);
                      if (!socket || currentRoom.hostId !== myPlayerId) return;
                      socket.emit('client:update-stand-apart-config', val);
                    }}
                    className="w-full px-4 py-2 border-2 border-fuchsia-300 rounded-lg focus:outline-none focus:border-fuchsia-500 bg-white"
                  >
                    {Array.from(
                      { length: STAND_APART_CARD_COUNT_MAX - STAND_APART_CARD_COUNT_MIN + 1 },
                      (_, i) => STAND_APART_CARD_COUNT_MIN + i,
                    ).map((count) => (
                      <option key={count} value={count}>
                        {count}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-600 mt-2">{t.games.standApart.cardCountHint}</p>
                </div>
              )}
            </div>
          )}

          {gameType === GameType.UNDERCOVER && (
            <UndercoverConfig
              socket={socket}
              currentRoom={currentRoom}
              undercoverCount={undercoverCount}
              blankCount={blankCount}
              mode={undercoverMode}
              factInputSeconds={factInputSeconds}
              onUndercoverCountChange={setUndercoverCount}
              onBlankCountChange={setBlankCount}
              onModeChange={setUndercoverMode}
              onFactInputSecondsChange={setFactInputSeconds}
              onFactTimerEditStart={() => setIsEditingFactTimer(true)}
              onFactTimerEditEnd={() => setIsEditingFactTimer(false)}
              configError={configError}
            />
          )}

          {gameType === GameType.DEADPAN && (
            <DeadpanConfig
              socket={socket}
              currentRoom={currentRoom}
              explanationTime={explanationTime}
              prepTime={prepTime}
              timerError={timerError}
              setExplanationTime={setExplanationTime}
              setPrepTime={setPrepTime}
              createTimerInputHandler={createTimerInputHandler}
            />
          )}

          {gameType === GameType.SIMILAR_MINDS && (
            <SimilarMindsConfig
              socket={socket}
              currentRoom={currentRoom}
              answerInputSeconds={similarMindsAnswerSeconds}
              votingSeconds={similarMindsVotingSeconds}
              votesPerPlayer={similarMindsVotesPerPlayer}
              maxVotesPerPlayer={similarMindsMaxVotesPerPlayer}
              firstQuestionHost={similarMindsFirstQuestionHost}
              onAnswerInputSecondsChange={setSimilarMindsAnswerSeconds}
              onVotingSecondsChange={setSimilarMindsVotingSeconds}
              onVotesPerPlayerChange={setSimilarMindsVotesPerPlayer}
              onFirstQuestionHostChange={setSimilarMindsFirstQuestionHost}
              onAnswerTimerEditStart={() => setIsEditingSimilarMindsAnswer(true)}
              onAnswerTimerEditEnd={() => setIsEditingSimilarMindsAnswer(false)}
              onVotingTimerEditStart={() => setIsEditingSimilarMindsVoting(true)}
              onVotingTimerEditEnd={() => setIsEditingSimilarMindsVoting(false)}
              configError={
                !isSimilarMindsConfigValid()
                  ? `${t.games.similarMinds.answerTimerHint} ${t.games.similarMinds.votingTimerHint} ${t.games.similarMinds.votesPerPlayerHint.replace('{max}', String(similarMindsMaxVotesPerPlayer))}`
                  : null
              }
            />
          )}

          {gameType === GameType.BOMB_RELAY && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <h3 className="text-base sm:text-lg font-bold text-red-800 mb-3 sm:mb-4 flex items-center gap-2">
                <span>💣</span>
                {t.games.bombRelay.settingsTitle}
              </h3>
              <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                {t.games.bombRelay.timerLabel}
              </label>
              <input
                type="number"
                min={BOMB_RELAY_TIMER_MIN}
                max={BOMB_RELAY_TIMER_MAX}
                value={bombRelayTime}
                onFocus={() => {
                  setIsEditingBombRelay(true);
                  setTimerError(null);
                }}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  const parsed = Number.isNaN(val) ? 0 : val;
                  setBombRelayTime(parsed);
                  if (!isValidBombRelayTimerValue(parsed)) {
                    setTimerError(bombRelayTimerErrorMessage);
                  } else {
                    setTimerError(null);
                    handleBombRelayTimerChange(parsed);
                  }
                }}
                onBlur={() => {
                  setIsEditingBombRelay(false);
                  handleBombRelayTimerChange(bombRelayTime);
                }}
                className="w-full px-4 py-2 border-2 border-red-300 rounded-lg focus:outline-none focus:border-red-500"
              />
              <p className="text-xs text-gray-600 mt-2">{t.games.bombRelay.timerRangeHint}</p>
              <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2 mt-4">
                {t.games.bombRelay.level1ProbabilityLabel}
              </label>
              <input
                type="number"
                min={BOMB_RELAY_LEVEL1_MIN}
                max={BOMB_RELAY_LEVEL1_MAX}
                value={bombRelayLevel1Probability}
                onFocus={() => {
                  setIsEditingBombRelayProbability(true);
                  setTimerError(null);
                }}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  const parsed = Number.isNaN(val) ? 0 : val;
                  setBombRelayLevel1Probability(parsed);
                  if (!isValidBombRelayLevel1Probability(parsed)) {
                    setTimerError(bombRelayLevel1ProbabilityErrorMessage);
                  } else {
                    setTimerError(null);
                    handleBombRelayLevel1ProbabilityChange(parsed);
                  }
                }}
                onBlur={() => {
                  setIsEditingBombRelayProbability(false);
                  handleBombRelayLevel1ProbabilityChange(bombRelayLevel1Probability);
                }}
                className="w-full px-4 py-2 border-2 border-red-300 rounded-lg focus:outline-none focus:border-red-500"
              />
              <p className="text-xs text-gray-600 mt-2">{t.games.bombRelay.level1ProbabilityRangeHint}</p>
              {timerError && (
                <p className="text-sm text-red-600 mt-2">{timerError}</p>
              )}
            </div>
          )}
          {gameType === GameType.PENSIEVE && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-purple-50 border-2 border-purple-200 rounded-xl">
              <h3 className="text-base sm:text-lg font-bold text-purple-800 mb-3 sm:mb-4">
                {t.games.pensieve.memoryWhoTitle}
              </h3>
              <select
                value={pensieveMemoryMode}
                onChange={(e) => handlePensieveModeChange(e.target.value as 'others' | 'self')}
                className="w-full px-4 py-2 border-2 border-purple-300 rounded-lg focus:outline-none focus:border-purple-500 bg-white"
              >
                <option value="others">{t.games.pensieve.memoryWhoOthers}</option>
                <option value="self">{t.games.pensieve.memoryWhoSelf}</option>
              </select>
              {pensieveMemoryMode === 'self' && (
                <>
                  <button
                    type="button"
                    onClick={handlePensieveNewOrder}
                    className="mt-3 w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700"
                  >
                    {t.games.pensieve.newOrderButton}
                  </button>
                  <div className="mt-3 text-xs sm:text-sm text-purple-700">
                    <span className="font-semibold">{t.games.pensieve.currentOrderLabel}: </span>
                    {(currentRoom.pensieveConfig?.selfDrawOrder ?? [])
                      .map((id) => currentRoom.players.find((p) => p.id === id)?.name)
                      .filter(Boolean)
                      .join(' → ') || '—'}
                  </div>
                </>
              )}
            </div>
          )}
          <div className={`p-4 rounded-xl mb-4 ${
            currentRoom.players.length >= minPlayersRequired && currentRoom.players.length <= maxPlayersForGame && isConfigValid
              ? 'bg-green-50 border-2 border-green-200'
              : 'bg-amber-50 border-2 border-amber-200'
          }`}>
            <p className={`font-semibold ${
              currentRoom.players.length >= minPlayersRequired && currentRoom.players.length <= maxPlayersForGame && isConfigValid ? 'text-green-800' : 'text-amber-800'
            }`}>
              {currentRoom.players.length < minPlayersRequired
                ? `⏳ ${formatString(t.ui.needMorePlayers, { count: String(minPlayersRequired - currentRoom.players.length) })}`
                : currentRoom.players.length > maxPlayersForGame
                ? `⏳ ${currentRoom.players.length} / ${maxPlayersForGame}`
                : !isConfigValid
                ? `⚠️ ${t.ui.fixConfiguration || 'Please fix configuration'}` 
                : `✅ ${t.ui.readyToStart}`}
            </p>
          </div>
          <button
            onClick={handleStartGame}
            disabled={currentRoom.players.length < minPlayersRequired || currentRoom.players.length > maxPlayersForGame || !isConfigValid}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
          >
            {currentRoom.players.length < minPlayersRequired 
              ? t.ui.waitingForPlayers 
              : `🚀 ${t.ui.startGame}`}
          </button>
        </div>
      )}
    </div>
  );
};
